/**
 * 첨부 blob URL 캐시 — 로그인 세션 수명 + 참조 카운트 + 상한 LRU.
 *
 * 다이얼로그를 여닫을 때마다 같은 이미지를 재다운로드하지 않게 한다.
 * 캐시 키가 첨부 id 뿐이므로 **로그아웃 시 반드시 비워야 한다** — 남기면
 * 같은 탭에서 계정을 전환한 다음 사용자가 BFF 가시성 검사 없이 이전
 * 사용자의 첨부를 캐시로 볼 수 있다. 정리는 **authStore 의 SIGNED_OUT
 * 처리**가 clearAttachmentUrlCache 로 수행한다(명시 로그아웃·refresh 실패
 * 등 모든 로그아웃 경로의 공통 지점 — useLogout 아님).
 *
 * 항목당 최대 첨부 상한(IssueLimit, 5 MiB)이라 무제한 보존하면 장수 세션(특히 admin 순회)에서 탭
 * 메모리가 무한히 자란다 — 상한 초과 시 축출하되, **렌더 중(참조 카운트
 * > 0)인 항목은 건너뛴다**: 32장 넘는 첨부를 가진 상세에서 앞쪽 이미지가
 * 뒤쪽 해석에 밀려 revoke 로 깨지면 안 된다 (리뷰 반영). 렌더러는
 * acquire/release 로 수명을 알린다.
 */

import { getIssueAttachment } from '@/app/services/issue/issueService';

const cache = new Map<string, Promise<string>>();
/** id → 현재 렌더 중인 소비자 수 — 0 이 아닌 항목은 축출 금지 */
const activeCounts = new Map<string, number>();

/** 상한 — 5 MiB × 32 = 최악 160 MiB. 초과분은 비활성 항목만 LRU 로 revoke·축출 */
export const ATTACHMENT_CACHE_MAX_ENTRIES = 32;

function revokeEntry(entry: Promise<string>): void {
  entry.then((url) => URL.revokeObjectURL(url)).catch(() => {});
}

function evictOverCap(): void {
  while (cache.size > ATTACHMENT_CACHE_MAX_ENTRIES) {
    let evicted = false;
    for (const key of cache.keys()) {
      if (!activeCounts.has(key)) {
        const entry = cache.get(key);
        cache.delete(key);
        if (entry) revokeEntry(entry);
        evicted = true;
        break;
      }
    }
    // 전부 활성이면 상한 초과를 허용 — 렌더 중 revoke 가 더 나쁘다
    if (!evicted) break;
  }
}

export function resolveAttachmentUrl(
  id: string,
  fetcher: typeof getIssueAttachment = getIssueAttachment,
): Promise<string> {
  let cached = cache.get(id);
  if (cached) {
    // LRU 갱신 — Map 삽입 순서가 곧 축출 순서
    cache.delete(id);
    cache.set(id, cached);
    return cached;
  }
  cached = fetcher(id).then(({ attachment, content }) => {
    // protobuf 버퍼의 뷰일 수 있어 독립 사본으로 Blob 을 만든다
    const bytes = new Uint8Array(content);
    return URL.createObjectURL(new Blob([bytes], { type: attachment.contentType }));
  });
  const pending = cached;
  // 실패는 캐시 안 함(재시도 가능). 단 clear 후 늦게 거절된 옛 요청이
  // 같은 id 로 캐시된 새 세션 항목을 지우지 않게, 여전히 자기 것일 때만
  pending.catch(() => {
    if (cache.get(id) === pending) cache.delete(id);
  });
  cache.set(id, cached);
  evictOverCap();
  return cached;
}

/** 렌더러 진입 — 해석과 동시에 참조를 잡아 축출로부터 보호한다. */
export function acquireAttachmentUrl(
  id: string,
  fetcher: typeof getIssueAttachment = getIssueAttachment,
): Promise<string> {
  activeCounts.set(id, (activeCounts.get(id) ?? 0) + 1);
  return resolveAttachmentUrl(id, fetcher);
}

/** 렌더러 이탈 — 마지막 참조가 풀리면 상한 초과분 축출을 재시도한다. */
export function releaseAttachmentUrl(id: string): void {
  const next = (activeCounts.get(id) ?? 0) - 1;
  if (next <= 0) {
    activeCounts.delete(id);
    evictOverCap();
  } else {
    activeCounts.set(id, next);
  }
}

/** 로그아웃/계정 전환 시 호출 — blob URL 을 회수하고 캐시를 비운다. */
export function clearAttachmentUrlCache(): void {
  for (const entry of cache.values()) {
    revokeEntry(entry);
  }
  cache.clear();
  activeCounts.clear();
}
